import { getStoredRealm } from "@/utils/auth-storage";
import { Box, Button, Card, CardContent } from "@mui/material";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Text from "../../../../../../../components/Text";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { CHART_COLORS } from "../../../../../../../components/app/provider/patient/chart";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useGetPatientPaymentV2 } from "../../../../../../../queries/billings/payments/use-get-patient-payment-v2";
import { useArchivePatientMedicineHistory } from "../../../../../../../queries/history/past-medication/use-patient-medication-history-archive";
import { PRIMITIVE, px } from "../../../../../../../tokens/design-tokens";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../../../../utils/utils";
import UpsertMedicalHistoryDrawer from "../history/UpsertMedicalHistoryDrawer";
const PatientBalance = () => {
  const [encounterPagination, setEncounterPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const patientData: any = useOutletContext();
  const { mutateAsync: archiveMedicineHistory } =
    useArchivePatientMedicineHistory();

  const medicationDisclosure = useDisclosure(false);
  const { refetch } = useGetPatientPaymentV2({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    patientUuid: patientData?.uuid,
  });

  const deletePastMedicationDisclosure = useDeleteDisClosure();

  // Summary card data (this would come from API in real implementation)
  const balanceSummary = {
    patientBalanceDue: 203.54,
    insuranceBalanceDue: 0.0,
    asOfDate: "31 Aug, 2025",
  };

  // Financial breakdown data (this would come from API in real implementation)
  const financialBreakdown = [
    {
      id: 1,
      billedAs: "Patient",
      payment: "20.00",
      adjustments: "0.00",
      writeOff: "0.00",
      due: "203.54",
      unusedPayment: "0.00",
      balanceDue: "203.54",
    },
    {
      id: 2,
      billedAs: "Insurance",
      payment: "70.00",
      adjustments: "25.00",
      writeOff: "0.00",
      due: "0.00",
      unusedPayment: "0.00",
      balanceDue: "0.00",
    },
    {
      id: 3,
      billedAs: "Total",
      payment: "80.00",
      adjustments: "25.00",
      writeOff: "0.00",
      due: "203.54",
      unusedPayment: "0.00",
      balanceDue: "203.54",
    },
  ];

  // Encounters data (this would come from API in real implementation)
  const encountersData = [
    {
      id: 1,
      encounterDate: "12-03-2024",
      total: 300,
      patientAmount: 200,
      insuranceAmount: 50,
      patientDue: 100,
      insuranceDue: 50,
    },
    {
      id: 2,
      encounterDate: "12-03-2024",
      total: 200,
      patientAmount: 50,
      insuranceAmount: 150,
      patientDue: 0.0,
      insuranceDue: 100,
    },
    {
      id: 3,
      encounterDate: "12-03-2024",
      total: 400,
      patientAmount: 300,
      insuranceAmount: 100,
      patientDue: 100,
      insuranceDue: 0.0,
    },
    {
      id: 4,
      encounterDate: "12-03-2024",
      total: 500,
      patientAmount: 200,
      insuranceAmount: 300,
      patientDue: 100,
      insuranceDue: 200,
    },
  ];

  // Financial breakdown table columns
  const FINANCIAL_BREAKDOWN_COLUMNS = [
    {
      field: "billedAs",
      headerName: "Billed As",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text
          isTabletext
          style={{
            fontWeight: PRIMITIVE.typography.fontWeight.regular,
            color: CHART_COLORS.textDarkMuted,
          }}
        >
          {row.billedAs}
        </Text>
      ),
    },
    {
      field: "payment",
      headerName: "Payment",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.payment}
        </Text>
      ),
    },
    {
      field: "adjustments",
      headerName: "Adjustments",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.adjustments}
        </Text>
      ),
    },
    {
      field: "writeOff",
      headerName: "Write-off",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.writeOff}
        </Text>
      ),
    },
    {
      field: "due",
      headerName: "Due",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.due}
        </Text>
      ),
    },
    {
      field: "unusedPayment",
      headerName: "Unused Payment",
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.unusedPayment}
        </Text>
      ),
    },
    {
      field: "balanceDue",
      headerName: "Balance Due",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.balanceDue}
        </Text>
      ),
    },
  ];

  // Encounters table columns
  const ENCOUNTERS_COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "encounterDate",
      flex: 1,
      minWidth: 120,
      headerName: "Visit Date",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          {row.encounterDate}
        </Text>
      ),
    },
    {
      field: "total",
      flex: 1,
      minWidth: 100,
      headerName: "Total",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.total}
        </Text>
      ),
    },
    {
      field: "patientAmount",
      flex: 1,
      minWidth: 120,
      headerName: "Patient Amount",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.patientAmount}
        </Text>
      ),
    },
    {
      field: "insuranceAmount",
      flex: 1,
      minWidth: 130,
      headerName: "Insurance Amount",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.insuranceAmount}
        </Text>
      ),
    },
    {
      field: "patientDue",
      flex: 1,
      minWidth: 100,
      headerName: "Patient Due",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.patientDue}
        </Text>
      ),
    },
    {
      field: "insuranceDue",
      flex: 1,
      minWidth: 110,
      headerName: "Insurance Due",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: CHART_COLORS.textDarkMuted }}>
          $ {row.insuranceDue}
        </Text>
      ),
    },
    {
      field: "action",
      flex: 1,
      minWidth: 140,
      headerName: "Action",
      sortable: false,
      renderCell: ({ row: _row }) => (
        <Button
          size="small"
          variant="outlined"
          // onClick={() =>}
          style={{
            fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
            color: CHART_COLORS.textDarker,
          }}
        >
          Make Payment
        </Button>
      ),
    },
  ]);

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveMedicineHistory({
      patientMedicalHistoryId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertMedicalHistoryDrawer disclosureHost={medicationDisclosure} />

      <DeleteModal
        w="380px"
        open={deletePastMedicationDisclosure.opened}
        onClose={deletePastMedicationDisclosure.close}
        id={deletePastMedicationDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Patient Balance"}
      />

      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          gap: px(PRIMITIVE.spacing[16]),
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Summary Cards Section */}
        <Box
          style={{
            display: "flex",
            gap: px(PRIMITIVE.spacing[8]),
            padding: px(PRIMITIVE.spacing[8]),
          }}
        >
          <Card
            style={{
              flex: 1,
              backgroundColor: CHART_COLORS.surfaceWhite,
              border: `1px solid ${CHART_COLORS.borderLight}`,
              borderRadius: px(PRIMITIVE.radius[4]),
              padding: px(PRIMITIVE.spacing[8]),
            }}
          >
            <CardContent style={{ padding: px(PRIMITIVE.spacing[8]) }}>
              <Text
                style={{
                  fontWeight: PRIMITIVE.typography.fontWeight.medium,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
                  color: CHART_COLORS.textMutedGrey,
                  textAlign: "center",
                  marginBottom: "6px",
                }}
              >
                Patient Balance Due as on {balanceSummary.asOfDate}
              </Text>
              <Text
                style={{
                  fontWeight: PRIMITIVE.typography.fontWeight.medium,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b3),
                  color: CHART_COLORS.primaryMid,
                  textAlign: "center",
                }}
              >
                {balanceSummary.patientBalanceDue}
              </Text>
            </CardContent>
          </Card>

          <Card
            style={{
              flex: 1,
              backgroundColor: CHART_COLORS.surfaceWhite,
              border: `1px solid ${CHART_COLORS.borderLight}`,
              borderRadius: px(PRIMITIVE.radius[4]),
              padding: px(PRIMITIVE.spacing[8]),
            }}
          >
            <CardContent style={{ padding: px(PRIMITIVE.spacing[8]) }}>
              <Text
                style={{
                  fontWeight: PRIMITIVE.typography.fontWeight.medium,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
                  color: CHART_COLORS.textMutedGrey,
                  textAlign: "center",
                  marginBottom: "6px",
                }}
              >
                Insurance Balance Due as on {balanceSummary.asOfDate}
              </Text>
              <Text
                style={{
                  fontWeight: PRIMITIVE.typography.fontWeight.medium,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b3),
                  color: CHART_COLORS.primaryMid,
                  textAlign: "center",
                }}
              >
                {balanceSummary.insuranceBalanceDue}
              </Text>
            </CardContent>
          </Card>
        </Box>
        {/* Financial Breakdown Table */}
        <CustomDataGridV2
          getRowId={(row) => row.id}
          rows={financialBreakdown}
          columns={FINANCIAL_BREAKDOWN_COLUMNS}
          hideFooter
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          sx={{
            border: `1px solid ${CHART_COLORS.borderLight}`,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: CHART_COLORS.surfaceSubtle,
              borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: CHART_COLORS.surfaceAlt,
            },
          }}
        />
        {/* Encounters Section */}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: px(PRIMITIVE.spacing[4]),
            width: "100%",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "4px 8px",
            }}
          >
            <Text
              style={{
                fontWeight: PRIMITIVE.typography.fontWeight.medium,
                fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
                color: CHART_COLORS.textDarker,
              }}
            >
              Encounters
            </Text>
          </Box>
          <CustomDataGridV2
            getRowId={(row) => row.id}
            rows={encountersData}
            columns={ENCOUNTERS_COLUMNS}
            disableRowSelectionOnClick
            paginationModel={encounterPagination}
            onPaginationModelChange={setEncounterPagination}
            autoHeight
            sx={{
              border: `1px solid ${CHART_COLORS.borderLight}`,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: CHART_COLORS.surfaceSubtle,
                borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: CHART_COLORS.surfaceAlt,
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default PatientBalance;
